import React, { useEffect, useId, useRef, useState } from 'react'
import './webchat.css'
import { ChatLauncher } from './ChatLauncher'
import { ChatPanel } from './ChatPanel'
import type {
  Operator,
  PresenceStatus,
  ViewMessage,
  WebChatLabels,
} from './types'

export type WebChatProps = {
  operator: Operator
  status: PresenceStatus
  statusLabel: string
  messages: ViewMessage[]
  labels: WebChatLabels
  typingLabel: string
  isTyping?: boolean
  unreadCount?: number
  greeting?: string
  composerDisabled?: boolean
  notice?: string
  aboveComposer?: React.ReactNode
  rootClassName?: string
  // Controlled (open + onOpenChange) or uncontrolled (defaultOpen).
  open?: boolean
  defaultOpen?: boolean
  onOpenChange?: (open: boolean) => void
  onSend?: (text: string) => void
}

export const WebChat: React.FC<WebChatProps> = ({
  operator,
  status,
  statusLabel,
  messages,
  labels,
  typingLabel,
  isTyping,
  unreadCount,
  greeting,
  composerDisabled,
  notice,
  aboveComposer,
  rootClassName,
  open,
  defaultOpen = false,
  onOpenChange,
  onSend,
}) => {
  const panelId = useId()
  const rootRef = useRef<HTMLDivElement>(null)
  const [internalOpen, setInternalOpen] = useState(defaultOpen)
  const isControlled = open !== undefined
  const isOpen = isControlled ? open : internalOpen

  const setOpen = (next: boolean) => {
    if (!isControlled) setInternalOpen(next)
    onOpenChange?.(next)
  }

  // Mobile fullscreen tracks the *visual* viewport so the soft keyboard
  // doesn't push the panel taller than the visible area. dvh/100vh follow the
  // layout viewport, which iOS Safari does not shrink for the keyboard.
  useEffect(() => {
    const vv = typeof window !== 'undefined' ? window.visualViewport : null
    const root = rootRef.current
    if (!isOpen || !vv || !root) return

    const apply = () => {
      root.style.setProperty('--wc-vh', `${vv.height}px`)
      root.style.setProperty('--wc-vv-top', `${vv.offsetTop}px`)
    }
    apply()
    vv.addEventListener('resize', apply)
    vv.addEventListener('scroll', apply)
    return () => {
      vv.removeEventListener('resize', apply)
      vv.removeEventListener('scroll', apply)
      root.style.removeProperty('--wc-vh')
      root.style.removeProperty('--wc-vv-top')
    }
  }, [isOpen])

  return (
    <div
      ref={rootRef}
      className={`wc-root ${isOpen ? 'wc-root--open' : ''} ${rootClassName ?? ''}`}
    >
      {isOpen && (
        <ChatPanel
          panelId={panelId}
          operator={operator}
          status={status}
          statusLabel={statusLabel}
          messages={messages}
          labels={labels}
          typingLabel={typingLabel}
          isTyping={isTyping}
          greeting={greeting}
          composerDisabled={composerDisabled}
          notice={notice}
          aboveComposer={aboveComposer}
          onClose={() => setOpen(false)}
          onSend={(text) => onSend?.(text)}
        />
      )}
      <ChatLauncher
        open={isOpen}
        unreadCount={unreadCount}
        panelId={panelId}
        openLabel={labels.launcherOpen}
        closeLabel={labels.launcherClose}
        onClick={() => setOpen(!isOpen)}
      />
    </div>
  )
}
